import { Company } from '@lx/shared/types';
import { WEBAPP_PREFIX } from '@lx/shared/storage';
import { QuoteToolInitParametersEnum } from '@lx/quote-tool/lite/shared/types';

/**
 * Saves the company to sessionStorage for persistence across page reloads.
 * @param company - The company code to store
 * @param companyStorageKey - The key used to store the company in sessionStorage
 */
export function saveCompanyToStorage(company: Company, companyStorageKey: string): void {
  const storageKey = `${WEBAPP_PREFIX}${companyStorageKey}`;
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(company));
  } catch (error) {
    console.warn('Failed to save company to sessionStorage', error);
  }
}

/**
 * Checks if the company has changed from what's stored in sessionStorage.
 * If a change is detected, sets the storeResetKey flag to trigger a store reset.
 * This ensures the application state is cleared when switching between companies
 * (e.g., from Alpadia to Kaplan) to prevent data inconsistencies.
 *
 * @param newCompany - The newly resolved company from URL or default
 * @param companyStorageKey - The key used to store the company in sessionStorage
 * @param storeResetKey - The key used to flag a store reset in sessionStorage
 */
export function handleCompanyChange(newCompany: Company, companyStorageKey: string, storeResetKey: string): void {
  const storedCompany = getCompanyFromStorage(WEBAPP_PREFIX, companyStorageKey);

  // If there's a stored company and it differs from the new one, flag for reset
  if (storedCompany && storedCompany !== newCompany) {
    try {
      sessionStorage.setItem(`${WEBAPP_PREFIX}${storeResetKey}`, JSON.stringify(true));
    } catch (error) {
      console.warn('Failed to set store reset flag', error);
    }
  }
}

/**
 * Resolves the application company based on priority:
 * 1. URL parameter or subdomain (highest priority)
 * 2. SessionStorage (persisted from previous page load)
 * 3. Default company (fallback)
 *
 * @param defaultCompany - The fallback company from environment configuration
 * @param companyStorageKey - The key used to store the company in sessionStorage
 * @param supportedCompanies - The list of companies that the application can serve (aligned with assets configured)
 * @returns The resolved company code
 */
export function resolveCompany(defaultCompany: Company, companyStorageKey: string, supportedCompanies: readonly Company[]): Company {
  // 1. Check company from URL (query param for local, subdomain for production)
  const companyFromUrl = getCompanyFromURL(supportedCompanies);
  if (companyFromUrl) {
    return companyFromUrl;
  }

  // 2. Check sessionStorage (persisted from previous session)
  const storedCompany = getCompanyFromStorage(WEBAPP_PREFIX, companyStorageKey);
  if (storedCompany && isValidCompany(storedCompany, supportedCompanies)) {
    return storedCompany;
  }

  // 3. Fall back to default
  return defaultCompany;
}

/**
 * Retrieves the company from sessionStorage.
 * @param storagePrefix - The prefix used for storage keys
 * @param companyStorageKey - The key used to store the company in sessionStorage
 * @returns The stored company or null if not found or invalid
 */
function getCompanyFromStorage(storagePrefix: string, companyStorageKey: string): Company | null {
  const storageKey = `${storagePrefix}${companyStorageKey}`;
  try {
    const rawStoredCompany = sessionStorage.getItem(storageKey);
    return rawStoredCompany && typeof rawStoredCompany === 'string' ? JSON.parse(rawStoredCompany) : null;
  } catch (error) {
    console.warn('Failed to retrieve company from sessionStorage', error);
    return null;
  }
}

/**
 * Retrieves the company from URL based on the current environment:
 * - Query param environments (localhost/127.0.0.1/temporary): Extracts from query parameter (?company=alpadia)
 * - Production/Stage environment: Extracts from subdomain (quote-tool-lite-alpadia-dev.klgdevint.com)
 *
 * Query param environments include:
 * - Local development: localhost, 127.0.0.1
 * - Temporary environments: URLs containing klg-XXXX pattern (e.g., static-dev.klgintdc.com/klg-14578/quote-tool-lite)
 *
 * @param supportedCompanies - The list of companies that the application can serve
 * @returns The company extracted from URL, or null if not found
 *
 * @example
 * // Local environment - returns 'alpadia' from query param
 * // URL: http://localhost:4200?company=alpadia
 * getCompanyFromURL(supportedCompanies) // returns 'alpadia'
 *
 * @example
 * // Temporary environment - returns 'alpadia' from query param
 * // URL: https://static-dev.klgintdc.com/klg-14578/quote-tool-lite?company=alpadia
 * getCompanyFromURL(supportedCompanies) // returns 'alpadia'
 *
 * @example
 * // Production environment - returns 'alpadia' from subdomain
 * // URL: https://quote-tool-lite-alpadia-dev.klgdevint.com
 * getCompanyFromURL(supportedCompanies) // returns 'alpadia'
 *
 * @example
 * // Production environment without company - returns null (uses default)
 * // URL: https://quote-tool-lite-dev.klgdevint.com
 * getCompanyFromURL(supportedCompanies) // returns null
 */
export function getCompanyFromURL(supportedCompanies: readonly Company[]): Company | null {
  const { hostname, pathname } = document.location;
  if (isQueryParamEnvironment(hostname, pathname)) {
    return getCompanyFromQueryParam(document.location.search, supportedCompanies);
  } else {
    return getCompanyFromSubdomain(hostname, supportedCompanies);
  }
}

/**
 * Determines if the application is running in an environment that uses query parameters
 * for company resolution instead of subdomain-based resolution.
 *
 * This includes:
 * - Local development (localhost, 127.0.0.1)
 * - Temporary environments (URLs containing klg-XXXX pattern, e.g., static-dev.klgintdc.com/klg-14578/quote-tool-lite)
 *
 * @param hostname - The current hostname
 * @param pathName - The current pathname (used to detect temporary environments)
 * @returns True if query parameters should be used for company resolution
 */
export function isQueryParamEnvironment(hostname: string, pathName: string): boolean {
  return isLocalEnvironment(hostname) || isTemporaryEnvironment(pathName);
}

/**
 * Determines if the application is running in a local development environment
 * @param hostname - The current hostname
 */
function isLocalEnvironment(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

/**
 * Determines if the application is running in a temporary environment
 * Temporary environments have URLs containing the pattern klg-XXXX (e.g., /klg-14578/)
 *
 * @param pathName - The current pathname
 * @returns True if running in a temporary environment
 */
function isTemporaryEnvironment(pathName: string): boolean {
  const temporaryEnvPattern = /\/klg-\d+\//;
  return temporaryEnvPattern.test(pathName);
}

/**
 * Type guard to validate if a string is a supported Company
 * @param value - The string to validate
 * @param supportedCompanies - The list of supported companies to validate against
 */
function isValidCompany(value: string, supportedCompanies: readonly Company[]): value is Company {
  return supportedCompanies.includes(value as Company);
}

/**
 * Extracts company from query parameter (used in local development)
 * @param queryParams - The query string to parse (e.g., "?company=alpadia")
 * @param supportedCompanies - The list of companies that the application can serve
 * @returns The extracted company or null if not found/invalid
 */
export function getCompanyFromQueryParam(queryParams: string, supportedCompanies: readonly Company[]): Company | null {
  const queryParamsMap = new URLSearchParams(queryParams);
  const companyParam = queryParamsMap.get(QuoteToolInitParametersEnum.Company);

  if (!companyParam) {
    return null;
  }

  const normalizedCompany = companyParam.trim().toLowerCase();

  return isValidCompany(normalizedCompany, supportedCompanies) ? normalizedCompany : null;
}

/**
 * Extracts the subdomain from a hostname
 * @param hostname - The full hostname (e.g., 'quote-tool-lite-alpadia-dev.klgdevint.com')
 * @returns The subdomain portion (e.g., 'quote-tool-lite-alpadia-dev')
 */
function extractSubdomain(hostname: string): string {
  return hostname.split('.')[0];
}

/**
 * Checks if a company identifier exists in the subdomain
 * Matches patterns like: -alpadia- or -alpadia (at end)
 *
 * @param subdomain - The subdomain to search
 * @param company - The company identifier to look for
 */
function subdomainContainsCompany(subdomain: string, company: Company): boolean {
  return subdomain.includes(`-${company}-`) || subdomain.endsWith(`-${company}`);
}

/**
 * Extracts company from subdomain (used in production environments)
 * Expected format: quote-tool-lite-{company}-{env}.domain.com
 * Also checks domain name for company (e.g., quote-tool-lite.alpadia.com)
 * If no company in subdomain or domain, returns null (will use default)
 *
 * @param hostname - The current hostname
 * @param supportedCompanies - The list of companies that the application can serve
 *
 * @example
 * // Returns 'alpadia'
 * getCompanyFromSubdomain('quote-tool-lite-alpadia-dev.klgdevint.com', supportedCompanies)
 *
 * @example
 * // Returns 'alpadia'
 * getCompanyFromSubdomain('quote-tool-lite.alpadia.com', supportedCompanies)
 *
 * @example
 * // Returns null (uses default company)
 * getCompanyFromSubdomain('quote-tool-lite-dev.klgdevint.com', supportedCompanies)
 */
export function getCompanyFromSubdomain(hostname: string, supportedCompanies: readonly Company[]): Company | null {
  const subdomain = extractSubdomain(hostname);

  // Check subdomain first (e.g., quote-tool-lite-alpadia-dev)
  const companyFromSubdomain = supportedCompanies.find((company) => subdomainContainsCompany(subdomain, company));

  if (companyFromSubdomain) {
    return companyFromSubdomain;
  }

  // Check domain name (e.g., quote-tool-lite.alpadia.com)
  return supportedCompanies.find((company) => hostname.includes(`.${company}.`)) ?? null;
}
